/**
 * 展示格式化工具（后台版）。
 *
 * 所有「数值 → 文案」的转换集中在这里，并且**区分两种缺失**：
 *   - 字段不存在（服务端按权限裁剪）→ 「无权限查看」
 *   - 字段为 null（确实没有数据）    → 「—」
 * 二者混用会让「看不到」与「没有」分不清，后台据此做判断会出错。
 */
import { bookingStatusLabels, memberLevels } from '@hew3/shared';
import type { ScopeKey } from './api.ts';

/** 无权限：字段未下发。**绝不用 0 或空字符串冒充。** */
export const NO_PERMISSION_TEXT = '无权限查看';

/** 有权限但库里确实没有值。 */
export const EMPTY_TEXT = '—';

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return EMPTY_TEXT;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY_TEXT;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 数值字段：`undefined` = 未下发（无权限），`null` = 无数据。 */
export function formatNumber(value: number | null | undefined): string {
  if (value === undefined) return NO_PERMISSION_TEXT;
  if (value === null) return EMPTY_TEXT;
  return String(value);
}

/** 文本字段：`undefined` = 未下发（无权限），`null`/空串 = 无数据。 */
export function formatText(value: string | null | undefined): string {
  if (value === undefined) return NO_PERMISSION_TEXT;
  if (value === null || value === '') return EMPTY_TEXT;
  return value;
}

/** 置信度：后端给的是 0–100 的数字，null 表示未评估。 */
export function formatConfidence(value: number | null | undefined): string {
  if (value === undefined) return NO_PERMISSION_TEXT;
  if (value === null) return EMPTY_TEXT;
  return `${value.toFixed(1)}%`;
}

export function formatPct(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

const SCOPE_LABELS: Record<string, string> = {
  all: '全部成员',
  guardianship: '仅守护关系',
  none: '不涉及成员个体数据',
};

/** 可见范围：展示文案随接口返回的范围键变化，不在页面里硬编码角色。 */
export function scopeLabel(scope: ScopeKey | string | null | undefined): string {
  if (scope === null || scope === undefined || scope === '') return EMPTY_TEXT;
  return SCOPE_LABELS[scope] ?? scope;
}

/**
 * 去掉服务端职责说明里的 Markdown 强调标记。
 * 后端把「**不看健康与财富明细**」直接写在文案里（用于接口与文档），
 * 界面上显示裸星号会很难看，因此这里只做最小清理：去星号、去起首空格。
 */
export function plainText(value: string | null | undefined): string {
  if (value === undefined) return NO_PERMISSION_TEXT;
  if (value === null || value === '') return EMPTY_TEXT;
  return value.replace(/\*\*/g, '').trim();
}

/**
 * 会籍级别中文名。
 *
 * 三个正式取值（`founding` / `core` / `standard`）的名称取自**共享口径** `memberLevels` ——
 * 不在这里另写一份：同一级别出现两个名字（例如 standard 一处叫「体验成员」、一处叫「联合体成员」）
 * 会让成员在 C 端与后台看到不同身份，是最难被发现的文案级缺陷。
 * 其余几个是历史演示数据里的旧取值，保留映射以便如实显示（不隐藏、不改写）。
 */
const LEGACY_LEVEL_LABELS: Record<string, string> = {
  elite: '至尊',
  pro: '尊享',
  basic: '基础',
};

export function levelLabel(level: string | null | undefined): string {
  if (level === undefined) return NO_PERMISSION_TEXT;
  if (level === null || level === '') return EMPTY_TEXT;
  const shared = memberLevels.find((l) => l.key === level);
  if (shared) return shared.name;
  return LEGACY_LEVEL_LABELS[level] ?? level;
}

const ALERT_LEVEL_LABELS: Record<string, string> = {
  danger: '三级',
  warn: '二级',
  info: '一级',
};

/** 等级名（三级 / 二级 / 一级），不含「预警」后缀 —— 调用处自己拼「三级预警」「三级级」这类文案要避免重复。 */
export function alertLevelLabel(level: string): string {
  return ALERT_LEVEL_LABELS[level] ?? level;
}

/** 等级全称（三级预警 / 二级预警 / 一级预警）。 */
export function alertLevelFull(level: string): string {
  return `${alertLevelLabel(level)}预警`;
}

/** 徽章色调：与 docs/权限模型.md 的等级口径一致（三级最重）。 */
export function alertLevelTone(level: string): 'danger' | 'warn' | 'ghost' {
  if (level === 'danger') return 'danger';
  if (level === 'warn') return 'warn';
  return 'ghost';
}

const ALERT_STATUS_LABELS: Record<string, string> = {
  open: '待处置',
  dispatched: '已派单',
  acknowledged: '已确认',
  resolved: '已解除',
};

export function alertStatusLabel(status: string): string {
  return ALERT_STATUS_LABELS[status] ?? status;
}

const TRUST_PLAN_LABELS: Record<string, string> = {
  basic: '基础守护',
  pro: '进阶守护',
  elite: '臻享守护',
};

export function trustPlanLabel(key: string | null | undefined): string {
  if (key === undefined) return NO_PERMISSION_TEXT;
  if (key === null || key === '') return EMPTY_TEXT;
  return TRUST_PLAN_LABELS[key] ?? key;
}

/** 短信通道状态：`console` 意味着验证码只写服务日志，**并未真正发出**。 */
export function smsChannelNotice(name: string): { label: string; warn: boolean; detail: string } {
  if (name === 'console') {
    return {
      label: 'console',
      warn: true,
      detail: '当前为开发通道：验证码只写入服务日志，未接入真实短信通道，成员与员工都收不到短信。',
    };
  }
  if (name === 'disabled') {
    return { label: name, warn: true, detail: '短信通道已显式关闭，任何短信都不会发出。' };
  }
  return { label: name, warn: false, detail: `短信通道已接入：${name}。` };
}

/** 从服务端错误文案里提取锁定剩余分钟（后端文案：请 N 分钟后重试）。 */
export function parseLockMinutes(message: string): number | null {
  const matched = /(\d+)\s*分钟/.exec(message);
  if (!matched) return null;
  const minutes = Number(matched[1]);
  return Number.isFinite(minutes) ? minutes : null;
}

/* ---------------- 报告复核（后台） ---------------- */

/**
 * 复核状态必须是**中文**：直接把 `ready` / `reviewing` 这类英文枚举显示给专家看，
 * 会被当成系统字段而不是「这份报告现在处于什么阶段」。
 */
const REVIEW_STATUS_LABELS: Record<string, string> = {
  ready: '待复核',
  reviewing: '复核中',
  reviewed: '已复核',
  failed: '生成失败',
};

/** 复核状态中文名。未知取值原样返回（不隐瞒服务端给了什么），空值回退到「未提供」。 */
export function reviewStatusLabel(status: string | null | undefined): string {
  if (status === null || status === undefined || status === '') return MISSING_TEXT;
  return REVIEW_STATUS_LABELS[status] ?? status;
}

/** 复核结论（`review_status` 列）：与「报告状态」是两件事，不能混用同一套文案。 */
const REVIEW_DECISION_LABELS: Record<string, string> = {
  pending: '待复核',
  approved: '复核通过',
  revise: '需修订',
};

export function reviewDecisionLabel(decision: string | null | undefined): string {
  if (decision === null || decision === undefined || decision === '') return MISSING_TEXT;
  return REVIEW_DECISION_LABELS[decision] ?? decision;
}

/** 复核状态的徽章色调：`reviewed` 才配绿色，`failed` 是失败而非「已完成」。 */
export function reviewStatusTone(status: string | null | undefined): 'ok' | 'warn' | 'danger' | 'ghost' {
  if (status === 'reviewed') return 'ok';
  if (status === 'ready' || status === 'reviewing') return 'warn';
  if (status === 'failed') return 'danger';
  return 'ghost';
}

/**
 * 字段未提供。
 *
 * 与 `EMPTY_TEXT`（`—`，有权限但库里确实没有值）刻意分开：
 * 报告正文里的字段缺失意味着「这一项没有依据」，用 `0` 或 `—` 都会被读成「值就是 0」。
 */
export const MISSING_TEXT = '未提供';

/** 服务端未下发的字段与值为 null 的字段，在报告正文里一律呈现为「未提供」。 */
export function reportField(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return MISSING_TEXT;
  return String(value);
}

/**
 * 产出方式：把接口给的**如实标识**翻成一句人话。
 *
 * 这是整个报告中心最容易被写坏的一处文案 —— 报告由三康螺旋引擎算数、规则模板成文，
 * 没有生成式模型参与，因此这里**只允许出现「算法组装」**，
 * 不允许出现「AI 生成」「大模型生成」这类不实表述（与 apps/api 的自测断言同一口径）。
 */
export function generatedByLabel(generatedBy: string | null | undefined): string {
  if (generatedBy === null || generatedBy === undefined || generatedBy === '') return MISSING_TEXT;
  if (generatedBy === 'sk-engine+rules') return '由三康螺旋引擎与规则组装';
  if (generatedBy === 'sk-engine+seed') return '由三康螺旋引擎与规则组装（演示种子预生成，内容与实时组装同源）';
  return `算法组装（产出标识：${generatedBy}）`;
}

/** 产出标识原文旁的注解：把「不是生成式模型产出」写明，避免被读成 AI 生成。 */
export function generatedByBadge(generatedBy: string | null | undefined): string {
  if (generatedBy === null || generatedBy === undefined || generatedBy === '') return MISSING_TEXT;
  return `算法组装 · ${generatedBy}`;
}

/** 引擎标定状态：false 时必须显式呈现「未校准」，不允许只用一个灰色小标签糊过去。 */
export function calibratedNotice(calibrated: boolean | null | undefined): {
  text: string;
  warn: boolean;
} {
  if (calibrated === undefined || calibrated === null) {
    return { text: '标定状态未提供', warn: true };
  }
  if (calibrated) return { text: '引擎已标定', warn: false };
  return { text: '未校准', warn: true };
}

/* ---------------- 权益核销（后台） ---------------- */

const BENEFIT_STATUS_LABELS: Record<string, string> = {
  active: '可用',
  used_up: '已用完',
  expired: '已过期',
};

/** 权益状态中文名。未知取值原样返回（不隐瞒服务端给了什么）。 */
export function benefitStatusLabel(status: string | null | undefined): string {
  if (status === null || status === undefined || status === '') return MISSING_TEXT;
  return BENEFIT_STATUS_LABELS[status] ?? status;
}

/** 状态色调：已过期与已用完都是「不能用」，但原因不同，颜色也不重合。 */
export function benefitStatusTone(status: string | null | undefined): 'ok' | 'warn' | 'danger' | 'ghost' {
  if (status === 'active') return 'ok';
  if (status === 'used_up') return 'warn';
  if (status === 'expired') return 'danger';
  return 'ghost';
}

/**
 * 「剩余」文案 —— 本页最容易写错的一处。
 *
 * **已过期与「剩余 0 次」必须长得完全不同**：
 * 过期时服务端给的是 `remaining: null`（不是 0），若界面上把它显示成「剩余 0 次」，
 * 操作人会读成「额度已被核销完」，而真实原因是**清零规则到期**（当年未用即归零）。
 * 两句话对应两种完全不同的处理方式（前者无需处理，后者要查流水），因此文案不许混用。
 */
export function benefitRemainingText(item: {
  remaining: number | null;
  unit: string;
  expired: boolean;
  status: string;
}): string {
  if (item.expired || item.status === 'expired') return '已过期不可用';
  if (item.remaining === null || item.remaining === undefined) return MISSING_TEXT;
  if (item.remaining <= 0) return `剩余 0 ${item.unit}（已用完）`;
  return `剩余 ${item.remaining} ${item.unit}`;
}

/**
 * 清零倒计时文案。`daysLeft` **来自接口**（服务端按 `valid_to` 算好），
 * 界面只负责把它说清楚：到期日当天仍可用（0 天），负数才是已过期。
 */
export function daysLeftText(daysLeft: number | null | undefined): string {
  if (daysLeft === null || daysLeft === undefined) return MISSING_TEXT;
  if (daysLeft < 0) return `已过期 ${Math.abs(daysLeft)} 天`;
  if (daysLeft === 0) return '今日到期（当天仍可用）';
  return `剩余 ${daysLeft} 天`;
}

/** 有效期区间文案：两端的日期都来自接口，界面不推算。 */
export function validityText(validFrom: string | null | undefined, validTo: string | null | undefined): string {
  const from = formatDate(validFrom);
  const to = formatDate(validTo);
  if (from === EMPTY_TEXT && to === EMPTY_TEXT) return MISSING_TEXT;
  return `${from} 至 ${to}`;
}

/* ---------------- 埋点看板（后台） ---------------- */

/**
 * 事件名中文名。
 *
 * 与 `reviewStatusLabel` 同一做法：**能翻的翻，翻不了的原样显示**（调用处同时给出原始事件名）。
 * 界面上不认识的事件名照实露出，比隐藏它安全 —— 埋点名前端一改，界面不该假装没这回事。
 */
const EVENT_LABELS: Record<string, string> = {
  app_boot: '应用启动',
  page_view: '页面浏览',
  page_ready: '内容可见（页面上报）',
  funnel_step: '漏斗步骤',
  api_error: '接口错误',
};

export function analyticsEventLabel(name: string | null | undefined): string {
  if (name === null || name === undefined || name === '') return MISSING_TEXT;
  return EVENT_LABELS[name] ?? name;
}

/**
 * 漏斗与步骤的中文名。
 *
 * 名称对应 C 端 `trackFunnel()` 的调用（login / report_parse），**只是标签**：
 * 未登记的取值一律原样返回，并且调用处始终把原始 key 一并显示，
 * 因此漏斗增删不会让界面说出一个不存在的业务含义。
 */
const FUNNEL_LABELS: Record<string, string> = {
  login: '登录转化',
  report_parse: '报告解析',
};

const FUNNEL_STEP_LABELS: Record<string, string> = {
  view: '进入登录页',
  otp_requested: '请求验证码',
  logged_in: '登录成功',
  report_created: '创建报告',
  file_uploaded: '上传文件',
  parse_started: '开始解析',
};

export function funnelLabel(funnel: string | null | undefined): string {
  if (funnel === null || funnel === undefined || funnel === '') return MISSING_TEXT;
  return FUNNEL_LABELS[funnel] ?? funnel;
}

export function funnelStepLabel(step: string | null | undefined): string {
  if (step === null || step === undefined || step === '') return MISSING_TEXT;
  return FUNNEL_STEP_LABELS[step] ?? step;
}

/** 毫秒数（p50 / p90 / 最慢）。缺失显示「未提供」，**不补 0 毫秒**。 */
export function formatMs(value: number | null | undefined): string {
  if (value === null || value === undefined) return MISSING_TEXT;
  return `${value} ms`;
}

/**
 * 百分比：`null` / `undefined` 显示「未提供」。
 *
 * 为什么单独写一个：接口在样本量为 0 时会把 `rate` 算成 0（分母为 0 无法定义比率）。
 * 直接显示「0%」会被读成「达标率是零」；界面必须把「没有样本」与「样本全不达标」分开。
 */
export function formatRate(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return MISSING_TEXT;
  return `${value.toFixed(digits)}%`;
}

/** 达标率 = 达标数 / 样本数；样本数为 0 时不给出比率（回到「未提供」）。 */
export function ratioText(within: number, samples: number, digits = 1): string {
  if (samples <= 0) return MISSING_TEXT;
  return `${((within / samples) * 100).toFixed(digits)}%`;
}

/**
 * 样本是否足够。
 *
 * 判定由服务端给出（`sufficientSample`），**界面不自己设阈值** ——
 * 阈值一旦写死在两处，就会出现「接口说不足、界面说足够」的分裂。
 * 这里只把结论与样本量一并说出口；`sufficientSample` 未下发时按「未知」呈现并告警。
 */
export function sampleNotice(
  sufficient: boolean | null | undefined,
  samples: number | null | undefined,
): { text: string; warn: boolean } {
  const count = samples === null || samples === undefined ? MISSING_TEXT : String(samples);
  if (sufficient === null || sufficient === undefined) {
    return { text: `样本是否足够：${MISSING_TEXT}（接口未下发 sufficientSample，界面不代为判定）`, warn: true };
  }
  if (!sufficient) {
    return { text: `样本不足：接口判定 sufficientSample = false（本窗口仅 ${count} 个样本），据此下结论不成立。`, warn: true };
  }
  return { text: `样本足够：接口判定 sufficientSample = true（本窗口 ${count} 个样本）。`, warn: false };
}

/* ---------------- 资源准入审核（后台） ---------------- */

/** 准入状态 → 徽章色调。中文状态名一律用接口下发的 `statusLabel`，这里只管颜色。 */
export function admissionStatusTone(
  status: string | null | undefined,
): 'ok' | 'warn' | 'danger' | 'ghost' | 'e' | 'h' | 'w' {
  if (status === 'listed' || status === 'admitted') return 'ok';
  if (status === 'tier2_passed') return 'e';
  if (status === 'tier1_passed') return 'w';
  if (status === 'suspended') return 'warn';
  if (status === 'rejected' || status === 'revoked') return 'danger';
  return 'ghost';
}

/** 证照三态的色调：**未登记**也是问题（一级审的就是文件齐备），因此不是灰而是告警色。 */
export function licenseStateTone(state: string | null | undefined): 'ok' | 'warn' | 'danger' | 'ghost' {
  if (state === 'valid') return 'ok';
  if (state === 'none') return 'warn';
  if (state === 'expired') return 'danger';
  return 'ghost';
}

/**
 * 复评紧迫度文案。
 *
 * `daysUntilNextReview` 与两个布尔判定**全部来自接口**（服务端按 `next_review_at` 算好），
 * 界面不本地算天数：本地时间一跨时区就会与接口给出的判定分裂。
 * 「已逾期」必须显著 —— 持续动态评估未按时复评，本身就是不合格信号。
 */
export function reviewUrgencyNotice(urgency: {
  reviewOverdue: boolean;
  reviewSoon: boolean;
  daysUntilNextReview: number | null;
}): { text: string; tone: 'danger' | 'warn' | 'ghost' } {
  const days = urgency.daysUntilNextReview;
  if (urgency.reviewOverdue && days !== null) {
    return { text: `复评已逾期 ${Math.abs(days)} 天（须立即复评）`, tone: 'danger' };
  }
  if (urgency.reviewOverdue) return { text: '复评已逾期（须立即复评）', tone: 'danger' };
  if (urgency.reviewSoon && days !== null) return { text: `临近复评（剩余 ${days} 天）`, tone: 'warn' };
  if (urgency.reviewSoon) return { text: '临近复评', tone: 'warn' };
  if (days === null) return { text: '未安排复评', tone: 'ghost' };
  return { text: `未临近（剩余 ${days} 天）`, tone: 'ghost' };
}

/**
 * 可约状态文案：**「可约」与「不可约」必须把原因说出来**。
 *
 * 只说一句「不可约」，运营无法判断是「还在审」还是「已清退」——两者的下一步完全不同
 * （前者继续推进审核，后者不得再接入）。原因由状态决定，判据与 `core/resourceAdmission.ts`
 * 的 `BOOKABLE_SUBJECT_STATUSES` 同义。
 */
export function bookableNotice(item: {
  bookable: boolean;
  status: string;
  statusLabel: string;
}): { bookable: boolean; text: string; tone: 'ok' | 'danger' | 'warn'; reason: string } {
  if (item.bookable) return { bookable: true, text: '可约', tone: 'ok', reason: '' };
  const reason =
    item.status === 'rejected'
      ? '已否决（不合格），不得接入'
      : item.status === 'revoked'
        ? '已清退'
        : item.status === 'suspended'
          ? '已暂停'
          : item.status === 'admitted'
            ? '尚未上架'
            : ['draft', 'tier1_passed', 'tier2_passed'].includes(item.status)
              ? '未通过三级审核'
              : `当前状态：${item.statusLabel}`;
  return { bookable: false, text: `不可约（原因：${reason}）`, tone: item.status === 'suspended' ? 'warn' : 'danger', reason };
}

/** 工单状态中文名。取自共享口径 `bookingStatusLabels`，前端不另写一份。 */
const BOOKING_STATUS_LABELS: Record<string, string> = { ...bookingStatusLabels };

export function bookingStatusText(status: string | null | undefined): string {
  if (status === null || status === undefined || status === '') return MISSING_TEXT;
  return BOOKING_STATUS_LABELS[status] ?? status;
}

